import { Parser } from 'expr-eval';
import memoize from 'fast-memoize';

import { computed } from 'vue';

import { useCurrentCalculation } from '../composables/useCurrentCalculation';

import { usePricesStore } from '../store/prices';
import { useProductsStore } from '../store/products';
import { useRulesStore } from '../store/rules';
import { useItemsStore } from '../store/items';
import { useGrantsStore } from '../store/grants';

import type {
  ProductPricesByItemParams,
  Rules,
  ProductPriceById,
  AssignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlugProps,
  ProductOptionValueByItemAndProductOptionSlugParams,
  SelectionValueByItemParams,
  RuleByIdParams,
  ValueFunctionParams,
  IfParams,
  SubstringFunctionParams,
  MaxFunctionParams,
  MinFunctionParams,
  ProductValueByItemParams,
  GrantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlugParams,
  GrantLevelOptionValueByIdSchemaParams,
  RoundFunctionParams,
  AssignedProductQuantityByItemAndAssignedProductSlugParams,
  AssignedProductNameByItemAndAssignedProductSlugParams,
  AssignedProductValueByItemAndAssignedProductSlugParams,
  ProductNameByItemSchemaParams,
  ProductNameByIdSchema,
  IsSegmentAddedToCalculationParams,
  ProductPriceByCategoryAndProductOptionSlugAndProductOptionValueParams,
  SimpleProduct,
  JoinFunctionParams,
  ReplaceFunctionParams,
  PmtFunctionParams,
  ProductOption,
  ProductPriceByProductIdParams,
  SwitchParams,
  ProductOptionValueByProductIdAndProductOptionSlugParams,
  CalculationDateParams,
} from '../utils/types';

import { getValidNumber } from '../utils/math';

export function useCalculationEngine(errorMessage?: string) {
  const pricesStore = usePricesStore();
  const productsStore = useProductsStore();
  const rulesStore = useRulesStore();
  const itemsStore = useItemsStore();
  const grantsStore = useGrantsStore();

  const {
    getProductItemById,
    getValueItemById,
    isSegmentAddedInCurrentCalculation,
    currentCalculation,
  } = useCurrentCalculation();

  const currentCalculationSeletedItemsValues = computed(() =>
    currentCalculation.value?.calculation_segments
      .flatMap((segment) => segment.calculation_segment_items)
      .map((item) => {
        if ('value' in item) {
          return item.value;
        }

        if ('product_id' in item) {
          return item.product_id;
        }

        if ('rule_id' in item) {
          return item.rule_id;
        }
        return null;
      })
  );

  function getOptionValue(option: ProductOption) {
    switch (option.type) {
      case 'boolean':
        return option.value?.toUpperCase() === 'TRUE';

      case 'float':
        return option.value === null ? 0 : parseFloat(option.value.replaceAll(',', '.'));

      case 'integer':
        return option.value === null ? 0 : parseInt(option.value, 10);

      case 'string':
        return option.value === null ? "''" : `"${option.value}"`;

      default:
        throw new Error(`Invalid product option: ${JSON.stringify(option)}`);
    }
  }

  function evaluateValueFunction(params: ValueFunctionParams) {
    switch (params.type) {
      case 'boolean':
        return Boolean(params.string.toUpperCase() === 'TRUE');
      case 'float':
        return parseFloat(params.string.replace(',', '.'));
      case 'integer':
        return parseInt(params.string, 10);
      case 'string':
        if (params.string === null) {
          return "''";
        }

        return `"${params.string}"`;
      default:
        throw Error(`Błąd w parametrach value rule ${JSON.stringify(params)} `);
    }
  }

  function getProductPriceById(params: ProductPriceById) {
    const price = pricesStore.getPriceById(Number(params.price_id));

    if (!price) {
      throw Error(`Nie znaleziono ceny ${JSON.stringify(params)}`);
    }

    return price.price_float;
  }

  function getProductPriceByItem(params: ProductPricesByItemParams) {
    const item = getProductItemById(Number(params.item_id));

    if (item) {
      const price = memoizedGetProductPriceById({ price_id: item.product_price_id });

      return price;
    }

    throw Error(`Nie znaleziono product_price_by_item z parametrami ${JSON.stringify(params)}`);
  }

  function getProductPriceByProductId(params: ProductPriceByProductIdParams) {
    if (!currentCalculation.value) {
      throw Error('Brak aktualnej kalkulacji');
    }

    const price = productsStore.getProductPriceByDate(
      Number(params.product_id),
      currentCalculation.value.calculation_created_at
    );

    if (price === null) {
      throw Error(`Nie znaleziono price ${JSON.stringify(params)}`);
    }

    return price;
  }

  function getItemProduct(itemId: number) {
    const item = getProductItemById(itemId);

    if (item) {
      const product = productsStore.getProductById(item.product_id);

      return product || null;
    }

    return null;
  }

  function getProductGroup(itemId: number, groupSlug: string) {
    const product = getItemProduct(itemId);

    if (product) {
      return product.product_groups.find((group) => group.slug === groupSlug) || null;
    }

    return null;
  }

  function getAssignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlug(
    params: AssignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlugProps
  ) {
    const productGroup = getProductGroup(Number(params.item_id), params.assigned_product_slug);

    const productOption = productGroup?.product.product_options.find(
      (option) => option.slug === params.product_option_slug
    );

    if (productOption?.value) {
      return memoizedGetOptionValue(productOption);
    }

    throw Error(
      `Nie znalezniono AssignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlugProps ${JSON.stringify(params)}`
    );
  }

  function getProductOptionValueByItemAndProductOptionSlug(
    params: ProductOptionValueByItemAndProductOptionSlugParams
  ) {
    const product = getItemProduct(Number(params.item_id));

    const currentOption = product?.product_options.find(
      (option) => option.slug === params.product_option_slug
    );

    if (currentOption?.value) {
      return memoizedGetOptionValue(currentOption);
    }

    throw Error(
      `Nie znalezniono ProductOptionValueByItemAndProductOptionSlugParams ${JSON.stringify(params)}`
    );
  }

  function getProductOptionValueByProductIdAndProductOptionSlug(
    params: ProductOptionValueByProductIdAndProductOptionSlugParams
  ) {
    const product = productsStore.getProductById(Number(params.product_id));

    if (!product) {
      throw Error(`Nie znaleziono produktu ${JSON.stringify(params)}`);
    }

    const currentOption = product.product_options.find(
      (option) => option.slug === params.product_option_slug
    );

    if (currentOption?.value) {
      return memoizedGetOptionValue(currentOption);
    }

    throw Error(
      `Nie znaleziono ProductOptionValueByProductIdAndProductOptionSlugParams ${JSON.stringify(params)}`
    );
  }

  function getSelectionValueByItem(params: SelectionValueByItemParams) {
    try {
      const value = getValueItemById(Number(params.item_id))?.value;
      const unit = itemsStore.getItemValue(Number(params.item_id))?.value_unit;

      if (unit === '%') {
        return Number(value) / 100;
      }

      if (typeof getValidNumber(value) === 'number') {
        return getValidNumber(value);
      }

      return `"${value}"`;
    } catch (error) {
      throw Error(`Nie znalezniono SelectionValueByItemParams ${JSON.stringify(params)}`);
    }
  }

  function evaluateIfFunction(params: IfParams) {
    const conditionRule = rulesStore.getRuleById(Number(params.condition));

    if (!conditionRule) {
      throw new Error(`Błąd if ${JSON.stringify(params)}`);
    }

    const conditionResult = Boolean(evalRules(conditionRule.rules).toUpperCase() === 'TRUE');

    const nextRuleId = conditionResult ? params.true_statement : params.false_statement;
    const nextRule = rulesStore.getRuleById(Number(nextRuleId));

    if (!nextRule) {
      throw new Error(`Błąd if ${JSON.stringify(params)}`);
    }

    const value = evalRules(nextRule.rules);

    if (typeof getValidNumber(value) === 'number') {
      return getValidNumber(value);
    }

    return `"${value}"`;
  }

  function evaluateSubstring(params: SubstringFunctionParams) {
    const substringRule = rulesStore.getRuleById(Number(params.rule_id));

    if (!substringRule?.rules) {
      throw Error(`Błąd substring ${JSON.stringify(params)}`);
    }

    const string = evalRules(substringRule.rules);

    const offset = Number(params.offset);
    const length = Number(params.length);

    const startIndex = offset < 0 ? string.length + offset : offset;
    const endIndex = length > 0 ? startIndex + length : undefined;

    return `"${string.slice(startIndex, endIndex)}"`;
  }

  function evaluateSwitch(params: SwitchParams) {
    const expressionValue = memoEvaluateRuleById(
      { rule_id: Number(params.expression) },
      currentCalculationSeletedItemsValues.value
    );

    for (let i = 0; i < params.key_value_rule_ids.length; i++) {
      const ruleKeyValue = memoEvaluateRuleById(
        { rule_id: Number(params.key_value_rule_ids[i].key) },
        currentCalculationSeletedItemsValues.value
      );

      if (String(ruleKeyValue) === String(expressionValue)) {
        return memoEvaluateRuleById(
          { rule_id: Number(params.key_value_rule_ids[i].value) },
          currentCalculationSeletedItemsValues.value
        );
      }
    }

    return memoEvaluateRuleById(
      { rule_id: Number(params.default_rule_id) },
      currentCalculationSeletedItemsValues.value
    );
  }

  function calculateDate(params: CalculationDateParams) {
    if (!currentCalculation.value) {
      throw Error('Brak aktualnej kalkulacji w calculateDate');
    }

    const calculationDate = new Date(currentCalculation.value.calculation_created_at);

    if (Number.isNaN(calculationDate.getTime())) {
      throw Error(
        `Nieprawidłowa data w kalkulacji: ${currentCalculation.value.calculation_created_at}`
      );
    }

    const resultDate = new Date(calculationDate);

    if (params.operator === 'add' || params.operator === 'subtract') {
      const daysRule = rulesStore.getRuleById(Number(params.rule_id));

      if (!daysRule) {
        throw Error(`Nie znaleziono reguły ${params.rule_id} w calculateDate`);
      }

      const daysValue = Number(evalRules(daysRule.rules));

      if (Number.isNaN(daysValue)) {
        throw Error(`Wartość dni w calculateDate nie jest liczbą: ${daysValue}`);
      }

      const daysToAdd = params.operator === 'add' ? daysValue : -daysValue;
      resultDate.setDate(resultDate.getDate() + daysToAdd);
    }

    const year = resultDate.getFullYear();
    const month = String(resultDate.getMonth() + 1).padStart(2, '0');
    const day = String(resultDate.getDate()).padStart(2, '0');

    return `"${year}-${month}-${day}"`;
  }

  function evaluateRuleById(
    params: RuleByIdParams,
    itemsValue: Array<number | string | boolean | null> | undefined
  ) {
    const innerRule = rulesStore.getRuleById(Number(params.rule_id));

    if (!innerRule) {
      throw Error(`Błąd rule_by_id ${JSON.stringify(params)}`);
    }

    const value = evalRules(innerRule.rules);

    if (typeof getValidNumber(value) === 'number') {
      return getValidNumber(value);
    }

    return `"${value}"`;
  }

  const memoEvaluateRuleById = memoize(evaluateRuleById);

  function getEvaluatedRuleById(ruleId: number) {
    const rule = rulesStore.getRuleById(ruleId);

    if (!rule) {
      throw Error(`Nie znaleziono reguły ${ruleId}`);
    }

    return evalRules(rule.rules);
  }

  function mapRuleIdsToValues(ruleIds: (number | string)[]) {
    return ruleIds.map((num) => {
      return getEvaluatedRuleById(Number(num));
    });
  }

  function evaluateMax(params: MaxFunctionParams) {
    const numbers = mapRuleIdsToValues(params.rule_ids).map((num) => Number(num));

    return Math.max(...numbers);
  }

  function evaluateMin(params: MinFunctionParams) {
    const numbers = mapRuleIdsToValues(params.rule_ids).map((num) => Number(num));

    return Math.min(...numbers);
  }

  function evaluateJoin(params: JoinFunctionParams) {
    const values = mapRuleIdsToValues(params.rule_ids);

    const string = values.join('').replaceAll(/"/g, '');

    if (typeof getValidNumber(string) === 'number') {
      return getValidNumber(string);
    }

    return `"${string}"`;
  }

  function evaluateReplace(params: ReplaceFunctionParams) {
    const search = getEvaluatedRuleById(Number(params.search_rule_id));
    const replace = getEvaluatedRuleById(Number(params.replace_rule_id));
    const subject = getEvaluatedRuleById(Number(params.subject_rule_id));

    return `"${subject.replaceAll(search, replace)}"`;
  }

  function getProductValueByItem(params: ProductValueByItemParams) {
    const product = getItemProduct(Number(params.item_id));

    if (!product?.value) {
      throw Error(`Nie znaleziono ProductValueByItemParams ${JSON.stringify(params)}`);
    }

    if (typeof getValidNumber(product.value) === 'number') {
      return getValidNumber(product.value);
    }

    return `"${product.value}"`;
  }

  function getGrantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlug(
    params: GrantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlugParams
  ) {
    const grant = grantsStore.getGrantById(Number(params.grant_id));
    const product = getItemProduct(Number(params.item_id));

    const grantLevel = grant?.grant_levels.find((level) => level.slug === product?.value);
    const grantLevelOption = grantLevel?.grant_level_options.find(
      (option) => option.slug === params.grant_level_option_slug
    );

    if (!grantLevelOption?.value) {
      throw Error(
        `Nie znaleziono GrantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlugParams ${JSON.stringify(params)}`
      );
    }

    if (typeof getValidNumber(grantLevelOption.value) === 'number') {
      return getValidNumber(grantLevelOption.value);
    }

    return `"${grantLevelOption.value}"`;
  }

  function getGrantLevelOptionValueById(params: GrantLevelOptionValueByIdSchemaParams) {
    const grantLevelOption = grantsStore.getGrantLevelOptionBtId(
      Number(params.grant_level_option_id)
    );

    if (!grantLevelOption?.value) {
      throw Error(`Nie znaleziono GrantLevelOptionValueByIdSchemaParams ${JSON.stringify(params)}`);
    }

    if (typeof getValidNumber(grantLevelOption.value) === 'number') {
      return getValidNumber(grantLevelOption.value);
    }

    return `"${grantLevelOption.value}"`;
  }

  function roundFunction(params: RoundFunctionParams) {
    const resultRule = rulesStore.getRuleById(Number(params.rule_id));

    if (!resultRule) {
      throw Error(`Nie znaleziono round rule ${JSON.stringify(params)}`);
    }

    const result = evalRules(resultRule.rules);
    const precision = Number(params.precision);
    const factor = 10 ** Math.abs(precision);

    if (params.type.toUpperCase() === 'UP') {
      if (precision > 0) {
        return Math.ceil(Number(result) * factor) / factor;
      }
      return Math.ceil(Number(result) / factor) * factor;
    }

    if (params.type.toUpperCase() === 'DOWN') {
      if (precision > 0) {
        return Math.floor(Number(result) * factor) / factor;
      }
      return Math.floor(Number(result) / factor) * factor;
    }

    if (params.type.toUpperCase() === 'DEFAULT') {
      if (precision >= 0) {
        return Number(result).toFixed(precision);
      }
      return Math.round(Number(result) / factor) * factor;
    }

    throw Error(`Nieobsługiwany typ zaokrąglania ${JSON.stringify(params)}`);
  }

  function getAssignedProductQuantityByItemAndAssignedProductSlug(
    params: AssignedProductQuantityByItemAndAssignedProductSlugParams
  ) {
    const productGroup = getProductGroup(Number(params.item_id), params.assigned_product_slug);

    const quantity = getValidNumber(productGroup?.quantity);

    if (typeof quantity === 'number') {
      return quantity;
    }

    throw Error(
      `Nie znaleziono AssignedProductQuantityByItemAndAssignedProductSlug ${JSON.stringify(params)}`
    );
  }

  function getAssignedProductPriceByItemAndAssignedProductSlug(
    params: AssignedProductQuantityByItemAndAssignedProductSlugParams
  ) {
    const productGroup = getProductGroup(Number(params.item_id), params.assigned_product_slug);

    const price = getValidNumber(productGroup?.product.product_price.price_float);

    if (typeof price === 'number') {
      return price;
    }

    throw Error(
      `Nie znaleziono AssignedProductQuantityByItemAndAssignedProductSlug ${JSON.stringify(params)}`
    );
  }

  function getAssignedProductNameByItemAndAssignedProductSlug(
    params: AssignedProductNameByItemAndAssignedProductSlugParams
  ) {
    const productGroup = getProductGroup(Number(params.item_id), params.assigned_product_slug);

    if (productGroup?.product.name) {
      return `"${productGroup.product.name}"`;
    }

    throw Error(
      `Nie znaleziono AssignedProductNameByItemAndAssignedProduct ${JSON.stringify(params)}`
    );
  }

  function getAssignedProductValueByItemAndAssignedProductSlug(
    params: AssignedProductValueByItemAndAssignedProductSlugParams
  ) {
    const productGroup = getProductGroup(Number(params.item_id), params.assigned_product_slug);

    if (productGroup?.product.value) {
      if (typeof getValidNumber(productGroup.product.value) === 'number') {
        return getValidNumber(productGroup.product.value);
      }

      return `"${productGroup.product.value}"`;
    }

    throw Error(
      `Nie znaleziono AssignedProductValueByItemAndAssignedProductSlugParams ${JSON.stringify(params)}`
    );
  }

  function getProductNameByItem(params: ProductNameByItemSchemaParams) {
    const product = getItemProduct(Number(params.item_id));

    if (product?.name) {
      return `"${product.name}"`;
    }

    throw Error(`Nie znaleziono ProductNameByItemSchemaParams ${JSON.stringify(params)}`);
  }

  function getProductZumNameByItem(params: ProductNameByItemSchemaParams) {
    const product = getItemProduct(Number(params.item_id));

    if (product?.zum_name) {
      return `"${product.zum_name}"`;
    }

    throw Error(`Nie znaleziono ProductZumNameByItemSchemaParams ${JSON.stringify(params)}`);
  }

  function getProductValueById(params: ProductNameByIdSchema) {
    const product = productsStore.getProductById(Number(params.product_id));

    if (product?.value) {
      if (typeof getValidNumber(product.value) === 'number') {
        return getValidNumber(product.value);
      }

      return `"${product.value}"`;
    }

    throw Error(`Nie znaleziono ProductValueById ${JSON.stringify(params)}`);
  }

  function getProductNameById(params: ProductNameByIdSchema) {
    const product = productsStore.getProductById(Number(params.product_id));

    if (product?.name) {
      return `"${product.name}"`;
    }

    throw Error(`Nie znaleziono ProductValueById ${JSON.stringify(params)}`);
  }

  function getProductZumNameById(params: ProductNameByIdSchema) {
    const product = productsStore.getProductById(Number(params.product_id));

    if (product?.zum_name) {
      return `"${product.zum_name}"`;
    }

    throw Error(`Nie znaleziono ProductValueById ${JSON.stringify(params)}`);
  }

  function isSegmentAddedToCalculation(params: IsSegmentAddedToCalculationParams) {
    return isSegmentAddedInCurrentCalculation(Number(params.segment_id));
  }

  function isProductByOptionSlugAndValue(
    product: SimpleProduct,
    product_option_slug: string,
    product_option_value: string | number
  ) {
    return product.product_options.some((option) => {
      const isSameSlug = option.slug === product_option_slug;
      const isSameValue = String(option.value) === String(product_option_value);

      return isSameSlug && isSameValue;
    });
  }

  function getProductPriceByCategoryAndProductOptionSlugAndProductOptionValue(
    params: ProductPriceByCategoryAndProductOptionSlugAndProductOptionValueParams
  ) {
    const categoryProducts = productsStore.getProductsByCategoryId(Number(params.category_id));

    const productOptionRule = rulesStore.getRuleById(Number(params.rule_id));

    if (!productOptionRule) {
      throw new Error(
        `Błąd getProductPriceByCategoryAndProductOptionSlugAndProductOptionValue, nie znalezniono rule ${JSON.stringify(params)}`
      );
    }

    const productOptionValue = parserEvaluate(productOptionRule.rules);

    const productByOptionSlugAndValue = categoryProducts.find((product) =>
      isProductByOptionSlugAndValue(product, params.product_option_slug, productOptionValue)
    );

    if (!currentCalculation.value) {
      throw Error(
        `Brak aktualnej kalkulacji w getProductPriceByCategoryAndProductOptionSlugAndProductOptionValue ${JSON.stringify(params)}`
      );
    }

    const price = productsStore.getProductPriceByDate(
      Number(productByOptionSlugAndValue?.id),
      currentCalculation.value.calculation_created_at
    );

    if (price === null) {
      throw Error(
        `Nie znaleziono ProductPriceByCategoryAndProductOptionSlugAndProductOptionValue ${JSON.stringify(params)}`
      );
    }

    return Number(price);
  }

  function evaluatePMTFunction(params: PmtFunctionParams) {
    const periodRate = Number(getEvaluatedRuleById(Number(params.rate_rule_id)));
    const periods = Number(getEvaluatedRuleById(Number(params.periods_rule_id)));
    const principal = Number(getEvaluatedRuleById(Number(params.principal_rule_id)));
    const future = Number(getEvaluatedRuleById(Number(params.future_value_rule_id)));

    const payment =
      (periodRate * (principal + future / (1 + periodRate) ** periods)) /
      (1 - (1 + periodRate) ** -periods);

    const value = Math.round(payment * 100) / 100;

    if (Number.isNaN(value)) {
      throw Error(`Calculation error in PMT: ${JSON.stringify(params)} resulted in NaN`);
    }

    return value;
  }

  const memoizedEvaluateValueFunction = memoize(evaluateValueFunction);
  const memoizedGetProductPriceById = memoize(getProductPriceById);
  const memoizedGetProductValueById = memoize(getProductValueById);
  const memoizedGetProductNameById = memoize(getProductNameById);
  const memoizedGetGrantLevelOptionValueById = memoize(getGrantLevelOptionValueById);
  const memoizedGetProductPriceByProductId = memoize(getProductPriceByProductId);
  const memoizedGetProductZumNameById = memoize(getProductZumNameById);
  const memoizedGetOptionValue = memoize(getOptionValue);

  function calculateRules(rules: Rules): string {
    try {
      return rules
        .map((rule) => {
          switch (rule.key) {
            case 'value':
              return memoizedEvaluateValueFunction(rule.data.params);

            case 'pmt':
              return evaluatePMTFunction(rule.data.params);

            case 'round':
              return roundFunction(rule.data.params);

            case 'product_price_by_item':
              return getProductPriceByItem(rule.data.params);

            case 'product_price_by_id':
              return memoizedGetProductPriceById(rule.data.params);

            case 'product_price_by_product_id':
              return memoizedGetProductPriceByProductId(rule.data.params);

            case 'assigned_product_option_value_by_item_and_assigned_product_slug_and_product_option_slug':
              return getAssignedProductOptionValueByItemAndAssignedProductSlugAndProductOptionSlug(
                rule.data.params
              );

            case 'product_option_value_by_item_and_product_option_slug':
              return getProductOptionValueByItemAndProductOptionSlug(rule.data.params);

            case 'product_option_value_by_product_id_and_product_option_slug':
              return getProductOptionValueByProductIdAndProductOptionSlug(rule.data.params);

            case 'selection_value_by_item':
              return getSelectionValueByItem(rule.data.params);

            case 'product_value_by_item':
              return getProductValueByItem(rule.data.params);

            case 'grant_level_option_value_by_grant_and_grant_level_item_and_grant_level_option_slug':
              return getGrantLevelOptionValueByGrantAndGrantLevelItemAndGrantLevelOptionSlug(
                rule.data.params
              );

            case 'grant_level_option_value_by_id':
              return memoizedGetGrantLevelOptionValueById(rule.data.params);

            case 'assigned_product_quantity_by_item_and_assigned_product_slug':
              return getAssignedProductQuantityByItemAndAssignedProductSlug(rule.data.params);

            case 'assigned_product_price_by_item_and_assigned_product_slug':
              return getAssignedProductPriceByItemAndAssignedProductSlug(rule.data.params);

            case 'assigned_product_name_by_item_and_assigned_product_slug':
              return getAssignedProductNameByItemAndAssignedProductSlug(rule.data.params);

            case 'assigned_product_value_by_item_and_assigned_product_slug':
              return getAssignedProductValueByItemAndAssignedProductSlug(rule.data.params);

            case 'product_name_by_item':
              return getProductNameByItem(rule.data.params);

            case 'product_zum_name_by_item':
              return getProductZumNameByItem(rule.data.params);

            case 'product_value_by_id':
              return memoizedGetProductValueById(rule.data.params);

            case 'product_name_by_id':
              return memoizedGetProductNameById(rule.data.params);

            case 'product_zum_name_by_id':
              return memoizedGetProductZumNameById(rule.data.params);

            case 'product_price_by_category_and_product_option_slug_and_product_option_value':
              return getProductPriceByCategoryAndProductOptionSlugAndProductOptionValue(
                rule.data.params
              );

            case 'is_segment_added_to_calculation':
              return isSegmentAddedToCalculation(rule.data.params);

            case 'max':
              return evaluateMax(rule.data.params);

            case 'min':
              return evaluateMin(rule.data.params);

            case 'join':
              return evaluateJoin(rule.data.params);

            case 'replace':
              return evaluateReplace(rule.data.params);

            case 'rule_by_id':
              return memoEvaluateRuleById(
                rule.data.params,
                currentCalculationSeletedItemsValues.value
              );

            case 'substring':
              return evaluateSubstring(rule.data.params);

            case 'if':
              return evaluateIfFunction(rule.data.params);

            case 'switch':
              return evaluateSwitch(rule.data.params);

            case 'calculation_date':
              return calculateDate(rule.data.params);

            default:
              if (rule.type === 'operator') {
                return rule.data.display_name;
              }

              throw new Error(`Unsupported rule type: ${JSON.stringify(rule)}`);
          }
        })
        .join(' ')
        .replace(/,/g, '.');
    } catch (error) {
      return `błąd obliczeń`;
    }
  }

  function parserEvaluate(rules: Rules) {
    const parser = new Parser();

    const expression = calculateRules(rules);

    return String(parser.evaluate(expression));
  }

  function evalRules(rules: Rules): string {
    try {
      const value = parserEvaluate(rules);

      return value;
    } catch (error) {
      console.error(error);
      console.log(rules);

      return errorMessage || 'błąd obliczeń';
    }
  }

  function evalRulesWithExpression(rules: Rules) {
    try {
      const parser = new Parser();

      const expression = calculateRules(rules);

      return {
        eval: String(parser.evaluate(expression)),
        expression,
      };
    } catch (error) {
      console.error(error);
      console.log(rules);

      const expression = calculateRules(rules);

      return {
        eval: errorMessage || 'błąd obliczeń',
        expression,
      };
    }
  }

  return {
    evalRules,
    evalRulesWithExpression,
  };
}
